// H1DetailChart.jsx — v9.1c
//
// H1-Detail-View für Split-View Pattern:
//   - User wählt Range auf Daily-Chart (Shift+Drag)
//   - Diese Komponente lädt H1-Candles für den Range via /api/regime-detail
//   - Rendert mit Lightweight Charts v5
//   - H1-Regime als Hintergrund-Bänder
//   - Trade-Marker (Pfeile am Entry, optional Exit-Linien)
//   - Eigener Zoom/Pan unabhängig vom Daily-Chart

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers, LineStyle } from "lightweight-charts";
import { theme, fonts } from "../../theme";
import { API_BASE } from "../../config";

function regimeColor(phase, trend) {
  if (phase === 0) return "rgba(148, 163, 184, 0.10)";
  if (phase === 2) return "rgba(251, 146, 60, 0.13)";
  if (phase === 1) {
    if (trend === 1)  return "rgba(22, 163, 74, 0.13)";
    if (trend === -1) return "rgba(220, 38, 38, 0.13)";
  }
  return "rgba(148, 163, 184, 0.05)";
}

function regimeLabel(phase, trend) {
  if (phase === 0) return "Undefined";
  if (phase === 2) return trend === 1 ? "Pivot Bull" : trend === -1 ? "Pivot Bear" : "Pivot";
  if (phase === 1) return trend === 1 ? "ETAB Bull" : trend === -1 ? "ETAB Bear" : "ETAB neutral";
  return "?";
}

function parseISO(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// Build H1 regime segments restricted to a date range
function buildH1Segments(transitions, periodEnd, rangeStart, rangeEnd) {
  if (!Array.isArray(transitions) || !transitions.length) return [];
  const segs = [];
  for (let i = 0; i < transitions.length; i++) {
    const t = transitions[i];
    const start = parseISO(t.ts);
    if (!start) continue;
    const end = i + 1 < transitions.length ? parseISO(transitions[i + 1].ts) : periodEnd;
    if (!end) continue;
    // Clip to range
    if (end < rangeStart || start > rangeEnd) continue;
    segs.push({
      start: start < rangeStart ? rangeStart : start,
      end: end > rangeEnd ? rangeEnd : end,
      phase: t.phase, trend: t.trend,
      color: regimeColor(t.phase, t.trend),
      label: regimeLabel(t.phase, t.trend),
    });
  }
  return segs;
}

// Filter trades to a date range
function filterTradesInRange(trades, rangeStart, rangeEnd) {
  if (!Array.isArray(trades)) return [];
  return trades.filter(tr => {
    const et = parseISO(tr.entry_time);
    return et && et >= rangeStart && et <= rangeEnd;
  });
}

export default function H1DetailChart({
  instrument,
  selectedRange,        // [Date, Date]
  h1Transitions,
  runEnd,
  trades,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const markersApiRef = useRef(null);
  const signalLinesRef = useRef([]);   // ISeriesApi[] für BoS/CHoCH-Linien
  const [chartReady, setChartReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bars, setBars] = useState([]);
  const [signals, setSignals] = useState([]);
  const [showSignals, setShowSignals] = useState(false);
  const [showTrades, setShowTrades] = useState(true);   // Trades default an
  const [bandPositions, setBandPositions] = useState([]);
  const [redrawTrigger, setRedrawTrigger] = useState(0);

  const [rangeStart, rangeEnd] = selectedRange || [null, null];

  // Filter trades to selected range
  const visibleTrades = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [];
    return filterTradesInRange(trades, rangeStart, rangeEnd);
  }, [trades, rangeStart, rangeEnd]);

  // Build H1 segments clipped to range
  const h1Segs = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [];
    return buildH1Segments(h1Transitions, runEnd, rangeStart, rangeEnd);
  }, [h1Transitions, runEnd, rangeStart, rangeEnd]);

  // Fetch H1 bars + (optional) signals when range changes
  useEffect(() => {
    if (!rangeStart || !rangeEnd || !instrument) {
      setBars([]);
      setSignals([]);
      return;
    }
    setLoading(true);
    setError(null);
    const base = API_BASE || "";
    const params = new URLSearchParams({
      instrument,
      start: rangeStart.toISOString(),
      end: rangeEnd.toISOString(),
      tf: "H1",
      with_signals: showSignals ? "true" : "false",
      _: Date.now().toString(),
    });
    fetch(`${base}/regime-detail?${params}`, { cache: "no-store" })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        setBars(d.bars || []);
        setSignals(d.signals || []);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [instrument, rangeStart?.getTime(), rangeEnd?.getTime(), showSignals]);

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current || !bars || !bars.length) {
      // Clean up previous chart if bars become empty
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
        markersApiRef.current = null;
        setChartReady(false);
      }
      return;
    }

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 520,
      layout: {
        // Transparent damit die H1-Regime-Bänder darunter durchscheinen
        background: { type: "solid", color: "rgba(0, 0, 0, 0)" },
        textColor: "#475569",
        fontFamily: fonts?.body || "system-ui",
      },
      grid: {
        vertLines: { color: "#f1f5f9" },
        horzLines: { color: "#f1f5f9" },
      },
      rightPriceScale: { borderColor: "#e2e8f0" },
      timeScale: {
        borderColor: "#e2e8f0",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "#94a3b8", labelBackgroundColor: "#1e293b" },
        horzLine: { color: "#94a3b8", labelBackgroundColor: "#1e293b" },
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#16a34a", downColor: "#dc2626",
      borderUpColor: "#16a34a", borderDownColor: "#dc2626",
      wickUpColor: "#16a34a", wickDownColor: "#dc2626",
      // v9.1c: Ganzzahl-Preise (Gold ist in vollen $)
      priceFormat: { type: "price", precision: 0, minMove: 1 },
    });

    // Bars are already {t: ms, o, h, l, c}
    const data = bars
      .map(b => ({
        time: Math.floor(b.t / 1000),
        open: b.o, high: b.h, low: b.l, close: b.c,
      }))
      .sort((a, b) => a.time - b.time);

    candleSeries.setData(data);

    chartRef.current = chart;
    seriesRef.current = candleSeries;
    setChartReady(true);

    chart.timeScale().fitContent();

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
        setRedrawTrigger(n => n + 1);
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersApiRef.current = null;
      setChartReady(false);
    };
  }, [bars]);

  const [labelTrigger, setLabelTrigger] = useState(0);
  const [signalLabels, setSignalLabels] = useState([]);

  // Trade-Marker (Entry-Arrow + Exit-Circle mit PnL) - keine Signal-Marker mehr,
  // Labels werden als HTML-Overlay mittig auf der Linie gerendert.
  useEffect(() => {
    if (!chartReady || !seriesRef.current || !chartRef.current) return;

    // 1. Trade-Markers
    const markers = [];
    if (showTrades) {
      for (const t of visibleTrades) {
        const entry = parseISO(t.entry_time);
        const exit = parseISO(t.exit_time);
        const isLong = t.direction === "long" || t.direction === "LONG" || t.direction === 1;
        const isWin = (t.pnl || 0) > 0;

        if (entry) {
          markers.push({
            time: Math.floor(entry.getTime() / 1000),
            position: isLong ? "belowBar" : "aboveBar",
            color: isWin ? "#16a34a" : "#dc2626",
            shape: isLong ? "arrowUp" : "arrowDown",
            text: isLong ? "L" : "S",
          });
        }
        if (exit) {
          markers.push({
            time: Math.floor(exit.getTime() / 1000),
            position: isLong ? "aboveBar" : "belowBar",
            color: isWin ? "#16a34a" : "#dc2626",
            shape: "circle",
            text: `${isWin ? "+" : "-"}$${Math.abs(t.pnl || 0).toFixed(0)}`,
          });
        }
      }
    }
    markers.sort((a, b) => a.time - b.time);
    try {
      if (markersApiRef.current) {
        markersApiRef.current.setMarkers(markers);
      } else {
        markersApiRef.current = createSeriesMarkers(seriesRef.current, markers);
      }
    } catch (e) {
      console.warn("Failed to set markers:", e);
    }

    // 2. Signal-Lines mit _sigMeta für Label-Positionierung
    for (const ls of signalLinesRef.current) {
      try { chartRef.current.removeSeries(ls); } catch {}
    }
    signalLinesRef.current = [];
    if (showSignals && signals.length > 0) {
      let added = 0;
      for (const s of signals) {
        const startDate = parseISO(s.level_start);
        const endDate = parseISO(s.time);
        if (!startDate || !endDate) continue;
        const t0 = Math.floor(startDate.getTime() / 1000);
        const t1 = Math.floor(endDate.getTime() / 1000);
        if (t1 <= t0) continue;
        const color = s.type === "CHoCH" ? "#2563eb" : "#7c3aed";
        try {
          const line = chartRef.current.addSeries(LineSeries, {
            color,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            lastValueVisible: false,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
            priceScaleId: "right",
          });
          line.setData([
            { time: t0, value: s.price },
            { time: t1, value: s.price },
          ]);
          line._sigMeta = { t0, t1, price: s.price, color, label: s.type };
          signalLinesRef.current.push(line);
          added++;
        } catch (e) {
          console.warn("[H1DetailChart] Could not add signal line:", e);
        }
      }
      console.log("[H1DetailChart] Rendered", added, "H1 signal lines");
    }
    setLabelTrigger(n => n + 1);
  }, [chartReady, visibleTrades, signals, showTrades, showSignals]);

  // Labels mittig auf den Linien als HTML-Overlay
  const updateSignalLabels = useCallback(() => {
    if (!chartRef.current || !seriesRef.current || signalLinesRef.current.length === 0) {
      setSignalLabels([]);
      return;
    }
    const timeScale = chartRef.current.timeScale();
    const series = seriesRef.current;
    const labels = [];
    for (const line of signalLinesRef.current) {
      const meta = line._sigMeta;
      if (!meta) continue;
      const y = series.priceToCoordinate(meta.price);
      if (y === null) continue;
      const tMid = Math.floor((meta.t0 + meta.t1) / 2);
      let xMid = timeScale.timeToCoordinate(tMid);
      let xStart = timeScale.timeToCoordinate(meta.t0);
      let xEnd = timeScale.timeToCoordinate(meta.t1);
      let x;
      if (xMid !== null) x = xMid;
      else if (xStart !== null && xEnd !== null) x = (xStart + xEnd) / 2;
      else if (xStart !== null) x = xStart;
      else if (xEnd !== null) x = xEnd;
      else continue;
      labels.push({ x, y, text: meta.label, color: meta.color });
    }
    setSignalLabels(labels);
  }, []);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    updateSignalLabels();
    const ts = chartRef.current.timeScale();
    const handler = () => updateSignalLabels();
    ts.subscribeVisibleTimeRangeChange(handler);
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(handler);
      ts.unsubscribeVisibleLogicalRangeChange(handler);
    };
  }, [chartReady, updateSignalLabels, labelTrigger, redrawTrigger]);

  // Update H1 regime band positions
  const updateBandPositions = useCallback(() => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const positions = h1Segs.map((seg, idx) => {
      const tStart = Math.floor(seg.start.getTime() / 1000);
      const tEnd   = Math.floor(seg.end.getTime() / 1000);
      const xStart = timeScale.timeToCoordinate(tStart);
      const xEnd   = timeScale.timeToCoordinate(tEnd);
      if (xStart === null || xEnd === null) return null;
      return {
        idx,
        left: Math.min(xStart, xEnd),
        width: Math.max(1, Math.abs(xEnd - xStart)),
        color: seg.color,
        seg,
      };
    }).filter(Boolean);
    setBandPositions(positions);
  }, [h1Segs]);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    updateBandPositions();
    const ts = chartRef.current.timeScale();
    const handler = () => updateBandPositions();
    ts.subscribeVisibleTimeRangeChange(handler);
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(handler);
      ts.unsubscribeVisibleLogicalRangeChange(handler);
    };
  }, [chartReady, updateBandPositions, redrawTrigger]);

  if (!selectedRange) {
    return (
      <div style={{padding:24, textAlign:"center", color:theme.text3, fontSize:12, background:theme.tint, borderRadius:8}}>
        <strong>H1 Detail</strong> — wähle einen Bereich im Daily-Chart oben mit <span style={{background:theme.white, padding:"1px 6px", borderRadius:3, fontFamily:fonts?.mono}}>Shift + Drag</span> um die H1-Struktur zu inspizieren.
      </div>
    );
  }

  return (
    <div>
      <div style={{display:"flex", alignItems:"center", gap:8, fontSize:11, color:theme.text3, marginBottom:8, paddingLeft:4, flexWrap:"wrap"}}>
        <span style={{fontWeight:700}}>H1 Detail:</span>
        <span style={{fontFamily:fonts?.mono}}>{rangeStart.toISOString().slice(0,10)} → {rangeEnd.toISOString().slice(0,10)}</span>
        <span style={{color:theme.text4}}>·</span>
        <span>{bars.length} H1-Bars · {visibleTrades.length} Trades{showSignals && signals.length > 0 ? ` · ${signals.length} Signals` : ""}</span>
        <button
          onClick={() => setShowTrades(t => !t)}
          style={{
            marginLeft: 12,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            border: `1px solid ${showTrades ? "#16a34a" : theme.border}`,
            borderRadius: 4,
            background: showTrades ? "#16a34a" : theme.white,
            color: showTrades ? "white" : theme.text2,
            cursor: "pointer",
          }}
        >
          {showTrades ? "✓ Trades" : "+ Trades"}
        </button>
        <button
          onClick={() => setShowSignals(s => !s)}
          style={{
            marginLeft: 4,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            border: `1px solid ${showSignals ? "#7c3aed" : theme.border}`,
            borderRadius: 4,
            background: showSignals ? "#7c3aed" : theme.white,
            color: showSignals ? "white" : theme.text2,
            cursor: "pointer",
          }}
        >
          {showSignals ? "✓ BoS/CHoCH" : "+ BoS/CHoCH"}
        </button>
        {loading && <span style={{marginLeft:"auto", color:theme.blueText}}>Lade…</span>}
        {error && <span style={{marginLeft:"auto", color:theme.redText}}>{error}</span>}
      </div>

      <div style={{position:"relative", borderRadius:8, border:`1px solid ${theme.divider}`, overflow:"hidden", background:theme.white}}>
        {/* Regime-Bänder Hintergrund */}
        <div style={{position:"absolute", top:0, left:0, right:0, height:520, pointerEvents:"none", zIndex:1}}>
          {bandPositions.map(b => (
            <div
              key={b.idx}
              style={{
                position:"absolute",
                top:0, height:"100%",
                left: b.left, width: b.width,
                background: b.color,
              }}
            />
          ))}
        </div>

        {/* Chart-Container */}
        <div ref={containerRef} style={{position:"relative", zIndex:2, width:"100%", height:520}}/>

        {/* BoS/CHoCH Labels mittig auf den Linien (HTML-Overlay) */}
        {signalLabels.map((lbl, i) => (
          <div
            key={`sig_${i}`}
            style={{
              position:"absolute",
              left: lbl.x,
              top: lbl.y,
              transform: "translate(-50%, -50%)",
              padding: "1px 5px",
              fontSize: 9,
              fontWeight: 700,
              fontFamily: fonts?.mono || "monospace",
              color: "white",
              background: lbl.color,
              borderRadius: 3,
              pointerEvents: "none",
              zIndex: 4,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}
          >
            {lbl.text}
          </div>
        ))}

        {/* Empty state */}
        {!loading && !error && bars.length === 0 && (
          <div style={{position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", color:theme.text3, fontSize:12, zIndex:3}}>
            Keine H1-Bars im gewählten Bereich.
          </div>
        )}
      </div>
    </div>
  );
}
